// Parses the game links the host accepts (plan.md §4, spec §6):
//   https://github.com/owner/repo[.git]
//   https://github.com/owner/repo/tree/<ref>[/<path>]
//   https://github.com/owner/repo/blob/<ref>/<path>/retrowser.json
//   https://github.com/owner/repo/commit/<sha>
//   owner/repo   owner/repo@<ref>   owner/repo@<ref>:<path>
// Scheme and "www." are optional for github.com URLs.

export interface GameLink {
  owner: string;
  repo: string;
  /** Branch, tag or commit. Undefined: the default branch (GitHub's HEAD). */
  ref?: string;
  /** Directory of the manifest inside the repo ("" = root). */
  path: string;
}

export type LinkResult = { ok: true; link: GameLink } | { ok: false; error: string };

const OWNER = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/;
const REPO = /^[A-Za-z0-9._-]{1,100}$/;
const REF = /^[A-Za-z0-9._\-/]{1,250}$/;
const SEG = /^[A-Za-z0-9._-]+$/;

function cleanPath(parts: string[]): string | null {
  const segs = parts.filter((s) => s !== '');
  if (segs.at(-1) === 'retrowser.json') segs.pop();
  for (const s of segs) if (!SEG.test(s) || s === '.' || s === '..') return null;
  return segs.join('/');
}

function checkRef(ref: string): boolean {
  return REF.test(ref) && !ref.includes('..') && !ref.startsWith('/') && !ref.endsWith('/') && !ref.includes('//');
}

function make(owner: string, repo: string, ref: string | undefined, path: string | null): LinkResult {
  repo = repo.replace(/\.git$/, '');
  if (!OWNER.test(owner)) return { ok: false, error: `"${owner}" is not a valid GitHub owner name` };
  if (!REPO.test(repo) || repo === '.' || repo === '..') return { ok: false, error: `"${repo}" is not a valid repository name` };
  if (ref !== undefined && !checkRef(ref)) return { ok: false, error: `"${ref}" is not a valid branch, tag or commit` };
  if (path === null) return { ok: false, error: 'the path in the link is not valid' };
  return { ok: true, link: { owner, repo, ...(ref ? { ref } : {}), path } };
}

/** Longest link accepted. */
export const MAX_LINK = 400;

export function parseLink(input: string): LinkResult {
  const s = input.trim();
  if (!s) return { ok: false, error: 'paste a GitHub repository link' };
  // Strict: printable ASCII only, no spaces, no backslashes, bounded length (no homoglyphs, control or bidi chars).
  if (s.length > MAX_LINK) return { ok: false, error: 'that link is too long' };
  if (!/^[\x21-\x7e]+$/.test(s) || s.includes('\\')) return { ok: false, error: 'the link has characters a GitHub link never has' };

  // Only https://github.com (or no scheme). No http, no user@, no port, no other host.
  const gh = /^(?:https:\/\/)?(?:www\.)?github\.com\/(.*)$/i.exec(s);
  if (gh) {
    let rest = gh[1];
    rest = rest.replace(/[?#].*$/, '');
    if (/%/.test(rest)) return { ok: false, error: 'the link must not contain encoded characters' };
    const parts = rest.split('/').map((p) => { try { return decodeURIComponent(p); } catch { return '\u0000'; } });
    const [owner, repo, kind, ref, ...more] = parts;
    if (!owner || !repo) return { ok: false, error: 'the link needs both an owner and a repository' };
    if (kind === undefined || kind === '') return make(owner, repo, undefined, '');
    if ((kind === 'tree' || kind === 'blob') && ref) return make(owner, repo, ref, cleanPath(more));
    if (kind === 'commit' && ref && /^[0-9a-f]{7,40}$/i.test(ref) && more.every((m) => m === '')) {
      return make(owner, repo, ref, '');
    }
    return { ok: false, error: 'use a repository link, or a /tree/<branch>/<folder> link' };
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return { ok: false, error: 'only https://github.com links are supported' };

  // owner/repo[@ref[:path]]
  const short = /^([^/@:\s]+)\/([^/@:\s]+)(?:@([^:\s]+))?(?::([^\s]*))?$/.exec(s);
  if (short) {
    const [, owner, repo, ref, path] = short;
    return make(owner, repo, ref, cleanPath((path ?? '').split('/')));
  }
  return { ok: false, error: 'that does not look like a GitHub repository link' };
}

/** Canonical host route for a link: /g/<owner>/<repo>?ref=..&path=.. */
export function gameRoute(l: GameLink): string {
  const q = new URLSearchParams();
  if (l.ref) q.set('ref', l.ref);
  if (l.path) q.set('path', l.path);
  const qs = q.toString();
  return `/g/${encodeURIComponent(l.owner)}/${encodeURIComponent(l.repo)}${qs ? '?' + qs : ''}`;
}

export function linkFromRoute(pathname: string, search: string): GameLink | null {
  const m = /^\/g\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  const q = new URLSearchParams(search);
  const r = make(decodeURIComponent(m[1]), decodeURIComponent(m[2]), q.get('ref') ?? undefined,
    cleanPath((q.get('path') ?? '').split('/')));
  return r.ok ? r.link : null;
}

export function manifestPath(l: GameLink): string {
  return l.path ? `${l.path}/retrowser.json` : 'retrowser.json';
}
